import { Component, OnInit, Input, OnDestroy, Output, EventEmitter } from '@angular/core';

import { PropalCartService } from '@service/propal-cart/propal-cart.service';
import { GridHeaderService } from '../service/grid.header.service';
import { PurchaseService } from '@service/purchase/purchase.service';
import { ArrayService } from '@service/utilities/array.service';
import { GridService } from '../service/grid.service';
import { BroadcastService } from '@service/broadcast/broadcast.service';
import { CustomToastrService } from '@service/toastr/custom-toastr.service';
import { ProgramService } from '@service/program/program.service';
import { ScheduleService } from '@service/schedule/schedule.service';

import { PropalCart } from '../../../resource/availability/propal-cart.resource';
import { OfferProgram } from '../../../resource/offerProgram.resource';
import { Broadcast } from 'src/app/resource/broadcast.resource';

import { GridExtendsComponent, RelatedOfferRow } from 'src/app/shared/class/grid-extends.component';
import { OfferProgramService } from 'src/app/service/offer-program/offer-program.service';
import { BroadcastPurchasedService } from '@service/broadcast-purchased/broadcast-purchased.service';

import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { forkJoin, Observable, of, Subject } from 'rxjs';

@Component({
  selector: 'app-offer-program-grid',
  templateUrl: './offer-program-grid.component.html',
  styleUrls: ['./offer-program-grid.component.scss'],
  providers: [ GridService, BroadcastService]
})
export class OfferProgramGridComponent extends GridExtendsComponent implements OnInit, OnDestroy {

  @Input() offerProgram: OfferProgram;
  @Input() propalCart: PropalCart;
  @Input() queryParams;
  @Input() filters;
  @Input() lastProgram: boolean;
  @Input() showRelatedOffers: boolean = false;
  @Input() showOfferInfoColumn: boolean = false;

  @Output() onSendBroadcast: EventEmitter<Broadcast[]> = new EventEmitter();

  destroyCompleteList$: Subject<boolean> = new Subject<boolean>();

  public loading: boolean;
  public gridData: any[];
  public broadcastsTimes: any;
  public schedules: any[];
  public header;
  public headerMonth;
  public broadcasts: Broadcast[];

  public hasAvailable :any;

  constructor(
    public broadcastService: BroadcastService,
    public gridService: GridService,
    public arrayService: ArrayService,
    public propalCartService: PropalCartService,
    public purchaseService: PurchaseService,
    public gridHeaderService: GridHeaderService,
    public customToastrService: CustomToastrService,
    public offerProgramService: OfferProgramService,
    public programService: ProgramService,
    public scheduleService: ScheduleService,
    private broadcastPurchasedService: BroadcastPurchasedService,
  ) {
    super(
      customToastrService,
      broadcastService,
      gridService,
      arrayService,
      purchaseService,
      propalCartService,
      gridHeaderService,
      offerProgramService,
      scheduleService
    );
  }

  ngOnInit() {
    this.setOfferProgramPropalCart();
    this.initQueryParams();

    forkJoin([
      this.getBroadcasts(),
      this.getRelatedOfferRows(),
      this.getSchedules(),
    ])
      .pipe(takeUntil(this.destroyCompleteList$))
      .subscribe(([, relatedOfferRows]) => {
        this.relatedOfferRows = relatedOfferRows;
        this.relatedBroadcastPurchasedIds = relatedOfferRows.reduce(
          (ids, row) => ids.concat(row.broadcastPurchasedIds), [] as number[]
        );
        this.getGameModules()
          .pipe(takeUntil(this.destroyCompleteList$))
          .subscribe(() => {
            this.displayGrid();
            this.hasAvailable = this.gridService.gethasAvailable();
          }, () => {
            this.loading = false;
          });
      }, () => {
        this.loading = false;
      });

    // update checked propal cells if offerProgram match
    this.propalCartService.offerProgramPropalSource$
      .pipe(takeUntil(this.destroyCompleteList$))
      .subscribe(offerProgramPropalCart => {
        if (!this.broadcasts || !offerProgramPropalCart) {
          return null;
        }

        if (offerProgramPropalCart.offerProgram.id === this.offerProgram.id) {
          this.offerProgramPropalCart = offerProgramPropalCart;
          if (this.gridData) {
            this.displayGrid();
            this.hasAvailable = this.gridService.gethasAvailable();
          }
        }
    }, error => {
      this.customToastrService.displayToastr('ERROR', 'Une erreur est survenue.');
    });

    // reset grid if propal cart is destroyed
    this.propalCartService.resetGridSource$
      .pipe(takeUntil(this.destroyCompleteList$))
      .subscribe(status => {
        if (status && this.broadcasts) {
          this.offerProgramPropalCart = null;
          if (this.gridData) {
            this.displayGrid();
          }
        }
      }, error => {
        this.customToastrService.displayToastr('ERROR', 'Une erreur est survenue.');
    });
  }

  ngOnDestroy() {
    this.destroyCompleteList$.next(true);
    this.destroyCompleteList$.unsubscribe();
  }

  private isEligibleNationalChannel(): boolean {
    const area = this.offerProgram && this.offerProgram.area;
    return !!area && area.id === 'NAT' && !!area.channel &&
      ['2', '3', '5'].indexOf(String(area.channel.id)) !== -1;
  }

  private getRelatedOfferRows(): Observable<RelatedOfferRow[]> {
    if (!this.showRelatedOffers || !this.isEligibleNationalChannel()) {
      return of([]);
    }

    return this.offerProgramService.getCompleteListByPages({
      related_to_offer_program_id: this.offerProgram.id,
      related_year: this.filters.year,
    }, 'offer-program-availibility').pipe(
      switchMap((offerPrograms: OfferProgram[]) => {
        const offerProgramsByOfferId: { [id: string]: OfferProgram[] } = {};
        const baseOfferId = String(this.offerProgram.offer.id);

        offerPrograms.forEach((offerProgram) => {
          if (!offerProgram.offer || offerProgram.offer.id == null) {
            throw new Error('Offre liée sans identifiant.');
          }
          const offerId = String(offerProgram.offer.id);
          if (offerId === baseOfferId) {
            return;
          }
          if (!offerProgramsByOfferId[offerId]) {
            offerProgramsByOfferId[offerId] = [];
          }
          if (!offerProgramsByOfferId[offerId].some((item) => String(item.id) === String(offerProgram.id))) {
            offerProgramsByOfferId[offerId].push(offerProgram);
          }
        });

        const groups = Object.keys(offerProgramsByOfferId)
          .map((offerId) => offerProgramsByOfferId[offerId])
          .sort((left, right) => String(left[0].offer.name || '').localeCompare(String(right[0].offer.name || '')));
        if (!groups.length) {
          return of([]);
        }

        const offerProgramIds = groups.reduce(
          (ids, group) => ids.concat(group.map((offerProgram) => String(offerProgram.id))), [] as string[]
        );
        return this.broadcastPurchasedService.getPurchasedOffersAvailabilityPurchases(
          offerProgramIds,
          this.filters.broadcasted_from,
          this.filters.broadcasted_to
        ).pipe(
          map((purchases) => {
            const purchasesByOfferProgramId: { [id: string]: any[] } = {};
            purchases.forEach((purchase) => {
              const ownerId = purchase.purchased_offer_program_id;
              if (ownerId == null || offerProgramIds.indexOf(String(ownerId)) === -1) {
                throw new Error('Offre achetée absente de la réponse des achats liés.');
              }
              const key = String(ownerId);
              if (!purchasesByOfferProgramId[key]) {
                purchasesByOfferProgramId[key] = [];
              }
              purchasesByOfferProgramId[key].push(purchase);
            });

            return groups.map((group) => {
              try {
                return this.buildRelatedOfferRow(
                  group,
                  group.map((offerProgram) => purchasesByOfferProgramId[String(offerProgram.id)] || []),
                  false
                );
              } catch (error) {
                console.error('Réponse incomplète pour les achats d’une offre liée.', error);
                return this.buildRelatedOfferRow(group, [], true);
              }
            });
          }),
          catchError((error) => {
            console.error('Impossible de récupérer les achats des offres liées.', error);
            return of(groups.map((group) => this.buildRelatedOfferRow(group, [], true)));
          })
        );
      }),
      tap((rows) => {
        if (rows.some((row) => row.purchaseLoadFailed)) {
          this.customToastrService.displayToastr('ERROR', 'Certains achats des offres liées n’ont pas pu être récupérés.');
        }
      }),
      catchError((error) => {
        console.error('Impossible de récupérer les offres liées à cette émission.', error);
        this.customToastrService.displayToastr('ERROR', 'Impossible de récupérer les offres liées à cette émission.');
        return of([]);
      })
    );
  }

  private buildRelatedOfferRow(
    offerPrograms: OfferProgram[], purchaseLists: any[][], purchaseLoadFailed: boolean
  ): RelatedOfferRow {
    const broadcastsById: { [id: string]: Broadcast } = {};
    const broadcastPurchasedIds: number[] = [];

    purchaseLists.forEach((purchases) => (purchases || []).forEach((broadcastPurchase) => {
      const relation = broadcastPurchase._embedded || broadcastPurchase;
      const broadcast = relation.broadcast;
      if (!broadcastPurchase.id || !broadcast || !broadcast.id || !(broadcast.start_time || broadcast.startTime) ||
        !relation.purchase || !relation.purchase._embedded || !relation.purchase._embedded.type) {
        throw new Error('Réponse incomplète pour un achat d’une offre liée.');
      }

      const purchasedId = Number(broadcastPurchase.id);
      if (broadcastPurchasedIds.indexOf(purchasedId) !== -1) {
        return;
      }
      broadcastPurchasedIds.push(purchasedId);

      if (!broadcastsById[broadcast.id]) {
        broadcastsById[broadcast.id] = new Broadcast(broadcast);
        broadcastsById[broadcast.id].broadcastPurchased = [];
      }
      broadcastsById[broadcast.id].broadcastPurchased.push(broadcastPurchase);
    }));

    return {
      offerPrograms: offerPrograms,
      broadcasts: Object.keys(broadcastsById).map((id) => broadcastsById[id]).sort((a, b) =>
        Date.parse(a.startTime) - Date.parse(b.startTime)
      ),
      broadcastPurchasedIds: broadcastPurchasedIds,
      purchaseLoadFailed: purchaseLoadFailed,
    };
  }
}
